import { Checkbox, DatePicker, TimePicker } from "components/ui"
import { Box, Button, StackView } from "@planningcenter/tapestry-react"
import classnames from "classnames"
import moment from "moment-timezone"
import { bool, func, number, oneOfType, shape, string } from "prop-types"

const DateTimeContainer = ({
  eventTime = {},
  canDeleteAllEventTimes = false,
  visibleEvenPosition,
  mobileBrowser,
  twentyFourHourTime,
  onlyEventTime = true,
  onChange
}) => {
  const { key, id, canBeDeleted, deleteMe, allDay, startsAt, endsAt } = eventTime

  const handleToggleAllDay = () => {
    onChange(key, { allDay: !allDay })
  }

  const classes = {
    row: true,
    hide: deleteMe === "1",
    "line-to-hide-js": true,
    "even-row": visibleEvenPosition
  }

  const handleTrashClick = event => {
    event.preventDefault()
    onChange(key, { deleteMe: "1" })
  }

  const handleStartsAtChange = newDateTime => {
    const newStartsAt = moment(newDateTime)
    const newEndsAt = moment(endsAt)

    if (newStartsAt.isSameOrAfter(endsAt)) {
      newEndsAt.add(newStartsAt.diff(startsAt), "milliseconds")
    }

    onChange(key, {
      startsAt: newStartsAt.format(),
      endsAt: newEndsAt.format()
    })
  }

  const handleEndsAtChange = newDateTime => onChange(key, { endsAt: moment(newDateTime).format() })

  const conditionalName = name => {
    if (id == null && deleteMe === "1") {
      return ""
    } else {
      return name
    }
  }

  return (
    <div className={classnames(classes)}>
      <input
        value={id}
        type="hidden"
        name={conditionalName("event[event_times_attributes][][id]")}
      />
      <input
        value={deleteMe}
        type="hidden"
        name={conditionalName("event[event_times_attributes][][_destroy]")}
      />
      <input
        value={startsAt}
        type="hidden"
        name={conditionalName("event[event_times_attributes][][starts_at]")}
      />
      <input
        value={endsAt}
        type="hidden"
        name={conditionalName("event[event_times_attributes][][ends_at]")}
      />
      <input
        value={allDay}
        type="hidden"
        name={conditionalName("event[event_times_attributes][][all_day]")}
      />
      <StackView
        className="time-container-row"
        axis={allDay ? "horizontal" : "vertical"}
        distribution={allDay ? "space-between" : "start"}
        mediaQueries={{
          sm: {
            axis: "horizontal",
            distribution: "space-between"
          }
        }}
      >
        <StackView
          axis="horizontal"
          flex={1}
          spacing={1}
          className="time-container-row__start"
          width="100%"
        >
          <Box flex={1}>
            <label htmlFor="event_event_times_attributes__starts_at_date">Start date</label>
            <DatePicker
              datetime={startsAt}
              onChange={handleStartsAtChange}
              id="event_event_times_attributes__starts_at_date"
            />
          </Box>
          {!allDay && (
            <Box flex={1}>
              <label htmlFor="event_event_times_attributes__starts_at_time">Start time</label>
              <TimePicker
                datetime={startsAt}
                twentyFourHourTime={twentyFourHourTime}
                onChange={handleStartsAtChange}
                mobileBrowser={mobileBrowser}
                id="event_event_times_attributes__starts_at_time"
              />
            </Box>
          )}
        </StackView>
        <StackView
          axis="horizontal"
          flex={1}
          spacing={1}
          className="time-container-row__end"
          width="100%"
        >
          {!allDay && (
            <Box
              flex={1}
              order={1}
              mediaQueries={{
                sm: {
                  order: 0
                },
                lg: {
                  order: 1
                },
                xl: {
                  order: 0
                }
              }}
            >
              <label htmlFor="event_event_times_attributes__ends_at_time">End time</label>
              <TimePicker
                datetime={endsAt}
                twentyFourHourTime={twentyFourHourTime}
                onChange={handleEndsAtChange}
                mobileBrowser={mobileBrowser}
                id="event_event_times_attributes__ends_at_time"
              />
            </Box>
          )}
          <Box flex={1}>
            <label htmlFor="event_event_times_attributes__ends_at_date">End date</label>
            <DatePicker
              datetime={endsAt}
              onChange={handleEndsAtChange}
              id="event_event_times_attributes__ends_at_date"
            />
          </Box>
        </StackView>
      </StackView>
      <StackView
        axis="horizontal"
        alignment="center"
        distribution="space-between"
        marginTop={1}
        marginBottom={2}
      >
        <Checkbox
          text={`All day`}
          htmlFor={`all_day_${key}`}
          onSelect={handleToggleAllDay}
          checked={allDay}
        />
        {(!onlyEventTime || canDeleteAllEventTimes) && (
          <Button
            onClick={handleTrashClick}
            theme="destroy"
            size="xs"
            disabled={!canBeDeleted}
            variant="outline"
            iconLeft={{
              name: "general.x",
              size: "xs"
            }}
            data-tooltip={
              !canBeDeleted
                ? "This date/time cannot be removed because it has already has registered attendees"
                : null
            }
            title="Remove"
          />
        )}
      </StackView>
    </div>
  )
}

DateTimeContainer.propTypes = {
  eventTime: shape({
    key: string,
    id: oneOfType([string, number]),
    deleteMe: string,
    startsAt: string,
    endsAt: string
  }),
  onChange: func,
  mobileBrowser: bool,
  onlyEventTime: bool,
  canDeleteAllEventTimes: bool,
  twentyFourHourTime: bool,
  visibleEvenPosition: bool
}

export default DateTimeContainer
